import React, { useEffect, useCallback } from 'react';
import { Container, Theme, SxProps, Stack, Alert } from '@mui/material';
import ReviewsList from '../components/Dashboard/ClinicalReviews/ReviewsList';
import { useScreenLargerThan } from '../lib/hooks/breakpoint-hooks';
import useTrackPageView from '../lib/hooks/use-track-page-view';
import useTrackEvent from '../lib/hooks/use-track-event';
import { Alarm } from '@mui/icons-material';
import useDashboardData from '../lib/hooks/use-dashboard-data';

const MemoizedReviewsList = React.memo(ReviewsList);

const DashboardPage = () => {
    const trackPageView = useTrackPageView();
    const trackEvent = useTrackEvent();
    const isLargerThanSmall = useScreenLargerThan('sm');
    const { fullData, urgentReviews, clinicalReviews, submittedReviews, loading, error, status } =
        useDashboardData();

    useEffect(() => {
        trackPageView('Dashboard Page');
    }, [trackPageView]);

    const trackDashboardData = useCallback(() => {
        if (!loading) {
            if (status >= 200 && status < 400) {
                trackEvent('Dashboard Page Data', {
                    status: 'success',
                    dashboardCases: fullData?.data.map((caseItem) => ({
                        caseNumber: caseItem.caseNumber,
                        caseStage: caseItem.caseStage,
                        isSubmitted: caseItem.isSubmitted,
                        inProgress: caseItem.inProgress
                    }))
                });
            } else {
                trackEvent('Dashboard Page Data', {
                    status: 'failure',
                    message: 'Dashboard API call response is neither 200 nor data exist'
                });
            }
        }
    }, [loading, status, trackEvent, fullData]);

    useEffect(() => {
        trackDashboardData();
    }, [trackDashboardData]);

    if (error) {
        return (
            <Alert severity="error">
                An error occurred while fetching data. Please try again later.
            </Alert>
        );
    }

    return (
        <Container sx={containerStyle(isLargerThanSmall)} maxWidth="xl">
            <Stack gap={3}>
                <MemoizedReviewsList
                    title={
                        <Stack direction={'row'} alignItems={'center'} gap={1} color={'red'}>
                            <Alarm />
                            Urgent & Expedited Reviews
                        </Stack>
                    }
                    data={urgentReviews}
                    subtitle="Review Immediately"
                    totalReviews={urgentReviews.length}
                    loading={loading}
                />
                <MemoizedReviewsList
                    title="Clinical Reviews"
                    data={clinicalReviews}
                    totalReviews={clinicalReviews.length}
                    loading={loading}
                />
                <MemoizedReviewsList
                    title="Submitted Reviews"
                    data={submittedReviews}
                    totalReviews={submittedReviews.length}
                    loading={loading}
                    defaultSortColumn="hasNewMessage"
                    defaultOrder="desc"
                    removeColumns={['inProgress']}
                />
            </Stack>
        </Container>
    );
};

const containerStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = { paddingY: 3 };
    const smallerThanSm: SxProps<Theme> = {
        paddingX: 0
    };

    return media ? biggerThanSm : { ...biggerThanSm, ...smallerThanSm };
};

export default DashboardPage;
